import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, X, ShieldCheck, UserPlus } from 'lucide-react';
import { auth, api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface TeamUser {
  id: string;
  name: string;
  email: string;
  role: string;
  status?: string;
}

/**
 * Account dialog — three sections:
 *   1. Change MY password (every authenticated user).
 *   2. Create a new user (admins only) — email + role + starting password.
 *   3. Reset a TEAMMATE's password (admins only).
 */
export function AccountDialog({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  // Esc closes the dialog (matches the X button + click-outside).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-20" onMouseDown={onClose}>
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-zinc-500" />
            <h2 className="text-sm font-bold text-zinc-900">Account &amp; users</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <ChangeOwnPassword />
          {isAdmin ? (
            <>
              <div className="my-5 border-t border-zinc-200" />
              <CreateUser />
              <div className="my-5 border-t border-zinc-200" />
              <AdminResetPassword />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ChangeOwnPassword() {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    setMsg(null);
    if (pw.length < 8) return setMsg({ ok: false, text: 'Password must be at least 8 characters.' });
    if (pw !== pw2) return setMsg({ ok: false, text: "The two passwords don't match." });
    setBusy(true);
    try {
      await auth.setPassword(pw);
      setMsg({ ok: true, text: 'Password updated. Use it next time you log in.' });
      setPw('');
      setPw2('');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message || 'Could not update password.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500">Change my password</h3>
      <p className="mt-0.5 text-[11px] text-zinc-500">
        Set your own password. Takes effect on your next login.
      </p>
      <div className="mt-3 space-y-2">
        <div>
          <Label className="text-xs">New password</Label>
          <Input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="At least 8 characters"
            className="mt-1 h-9 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Confirm new password</Label>
          <Input
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            placeholder="Type it again"
            className="mt-1 h-9 text-sm"
          />
        </div>
      </div>
      {msg ? (
        <p className={`mt-2 text-[11px] ${msg.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{msg.text}</p>
      ) : null}
      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={busy || !pw || !pw2} onClick={submit}>
          {busy ? 'Saving…' : 'Update password'}
        </Button>
      </div>
    </div>
  );
}

function CreateUser() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'OPERATOR' | 'ACCOUNTANT' | 'ADMIN'>('OPERATOR');
  const [team, setTeam] = useState<'GENERAL' | 'VISA'>('GENERAL');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    setMsg(null);
    if (!name.trim()) return setMsg({ ok: false, text: 'Enter a name.' });
    if (!email.includes('@')) return setMsg({ ok: false, text: 'Enter a valid email.' });
    if (pw.length < 8) return setMsg({ ok: false, text: 'Starting password must be at least 8 characters.' });
    setBusy(true);
    try {
      await auth.createUser({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role,
        team: role === 'OPERATOR' ? team : undefined,
        password: pw,
      });
      setMsg({ ok: true, text: `${name.trim()} created. Share the email + password with them.` });
      setName('');
      setEmail('');
      setPw('');
      setRole('OPERATOR');
      setTeam('GENERAL');
      qc.invalidateQueries({ queryKey: ['users'] });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message || 'Could not create user.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-zinc-500">
        <UserPlus className="h-3.5 w-3.5" />
        Create a new user
      </h3>
      <p className="mt-0.5 text-[11px] text-zinc-500">
        Admin only. New users sign in on the web with the email + password
        you set here. The mobile app stays admin-only.
      </p>
      <div className="mt-3 space-y-2">
        <div>
          <Label className="text-xs">Full name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Priya Sharma"
            className="mt-1 h-9 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@flygoldfinch.com"
            className="mt-1 h-9 text-sm"
          />
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <Label className="text-xs">Role</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as typeof role)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="OPERATOR">Operator</option>
              <option value="ACCOUNTANT">Accountant</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          {role === 'OPERATOR' ? (
            <div className="flex-1">
              <Label className="text-xs">Team</Label>
              <select
                value={team}
                onChange={(e) => setTeam(e.target.value as typeof team)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="GENERAL">General</option>
                <option value="VISA">Visa</option>
              </select>
            </div>
          ) : null}
        </div>
        <div>
          <Label className="text-xs">Starting password</Label>
          <Input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="At least 8 characters"
            className="mt-1 h-9 text-sm"
          />
        </div>
      </div>
      {msg ? (
        <p className={`mt-2 text-[11px] ${msg.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{msg.text}</p>
      ) : null}
      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={busy} onClick={submit}>
          {busy ? 'Creating…' : 'Create user'}
        </Button>
      </div>
    </div>
  );
}

function AdminResetPassword() {
  const { user } = useAuth();
  const [targetId, setTargetId] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const users = useQuery<TeamUser[]>({
    queryKey: ['users'],
    queryFn: () => api('/api/users'),
  });
  const resettable = (users.data ?? []).filter(
    (u) => u.id !== user?.id && (!u.status || u.status === 'ACTIVE'),
  );

  const submit = async () => {
    setMsg(null);
    if (!targetId) return setMsg({ ok: false, text: 'Pick a teammate first.' });
    if (pw.length < 8) return setMsg({ ok: false, text: 'Password must be at least 8 characters.' });
    setBusy(true);
    try {
      await auth.adminResetPassword(targetId, pw);
      const who = resettable.find((u) => u.id === targetId);
      setMsg({
        ok: true,
        text: `Password reset for ${who?.name ?? 'the user'}. They've been signed out — share the new password with them.`,
      });
      setPw('');
      setTargetId('');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message || 'Could not reset password.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-zinc-500">
        <ShieldCheck className="h-3.5 w-3.5" />
        Reset a teammate's password
      </h3>
      <p className="mt-0.5 text-[11px] text-zinc-500">
        Admin only. Use this when someone forgot their password. They get
        signed out and must log in with the new one.
      </p>
      <div className="mt-3 space-y-2">
        <div>
          <Label className="text-xs">Teammate</Label>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">— Select —</option>
            {resettable.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} · {u.role.toLowerCase()}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">New password for them</Label>
          <Input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="At least 8 characters"
            className="mt-1 h-9 text-sm"
          />
        </div>
      </div>
      {msg ? (
        <p className={`mt-2 text-[11px] ${msg.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{msg.text}</p>
      ) : null}
      <div className="mt-3 flex justify-end">
        <Button size="sm" variant="outline" disabled={busy || !targetId || !pw} onClick={submit}>
          {busy ? 'Resetting…' : 'Reset their password'}
        </Button>
      </div>
    </div>
  );
}
